import { createStore, createEvent, createEffect, sample } from 'effector';
import { axiosClient } from '@nx-frontend/shared/api';
import { NoteDto, NoteSearchResultItem } from '@nx-frontend/shared/types';

interface SearchResultState {
  entities: Record<string, NoteSearchResultItem[]>;
  notes: NoteDto[];
  folders: NoteDto[];
  query: string;
  isFetching: boolean;
  isLoaded: boolean;
  count: number | null;
  initial: boolean;
}

export interface FetchSearchParams {
  workspaceId: string;
  query: string;
  buildExcerpts: boolean;
  getMatches: boolean;
  counters: boolean;
  signal?: AbortSignal;
}

export const initialSearchResult: SearchResultState = {
  entities: {},
  notes: [],
  folders: [],
  query: '',
  isFetching: false,
  isLoaded: false,
  count: 0,
  initial: true,
};

export const fetchSearchResultFx = createEffect(
  async ({ workspaceId, query, buildExcerpts, getMatches, counters, signal }: FetchSearchParams) => {
    try {
      const { data } = await axiosClient.post<{
        entities: NoteSearchResultItem[];
        count: number;
        notes: NoteDto[];
        folders: NoteDto[];
        query: string;
        workspaceId: string;
      }>(
        `/v2/api/workspaces/${workspaceId}/search`,
        { query, buildExcerpts, getMatches, counters },
        {
          signal: signal,
        }
      );
      return data;
    } catch (err) {
      console.error(err);
      return {
        entities: [],
        count: 0,
        notes: [],
        folders: [],
        query,
        workspaceId,
      };
    }
  }
);
export const fetchSearchEvent = createEvent<FetchSearchParams>();
export const resetSearchEvent = createEvent();
export const reinitSearchEvent = createEvent();
export const startFetchSearchEvent = createEvent();

export const $searchResult = createStore<SearchResultState>(initialSearchResult);

$searchResult.on(startFetchSearchEvent, (_) => {
  return {
    ..._,
    notes: [],
    folders: [],
    entities: {},
    query: '',
    count: 0,
    initial: false,
    isFetching: true,
  };
});

sample({
  source: $searchResult,
  clock: fetchSearchEvent,
  filter: (state) => !(state.isFetching || state.isLoaded),
  fn: (_, params) => params,
  target: [startFetchSearchEvent, fetchSearchResultFx],
});

$searchResult.on(resetSearchEvent, (state) => {
  return {
    ...state,
    isLoaded: false,
  };
});
$searchResult.on(reinitSearchEvent, (state) => {
  return {
    ...state,
    entities: {},
    notes: [],
    folders: [],
    query: '',
    isFetching: false,
    isLoaded: false,
    count: 0,
    initial: true,
  };
});
$searchResult.on(fetchSearchResultFx.doneData, (_, payload) => {
  const initial = '' === payload.query ? true : false;
  const entities: Record<string, NoteSearchResultItem[]> = {};
  entities[payload.workspaceId] = payload.entities;
  return {
    entities,
    notes: payload.notes,
    folders: payload.folders,
    query: payload.query,
    isFetching: false,
    isLoaded: true,
    count: payload.count,
    initial,
  };
});

export interface SearchState {
  isShown?: boolean;
}

const initialModal: SearchState = {
  isShown: false,
};

export const openSearchEvent = createEvent<SearchState | void>();
export const closeSearchEvent = createEvent();
export const $SearchStore = createStore(initialModal)
  .on(openSearchEvent, () => {
    return {
      ...initialModal,
      isShown: true,
    };
  })
  .on(closeSearchEvent, (state) => {
    return {
      ...state,
      isShown: false,
    };
  });

export const $isShownModal = $SearchStore.map(({ isShown }) => isShown);

export const searchValueChanged = createEvent<string>();
export const $searchValue = createStore<string>('').on(searchValueChanged, (_, payload) => payload);
